import React, { useMemo } from 'react'
import { Typography } from '@material-ui/core'
import { Folder, UnitVisibility, deepCopyFolder } from '../../../../../models'
import { useStepLoriStyles } from '../styles'

const isScheduleVisible = (visibility: UnitVisibility): boolean =>
    visibility === UnitVisibility.ALWAYS_VISIBLE ||
    visibility === UnitVisibility.SCHEDULE_ONLY

interface AdvancedLoriTreeProps {
    infrastructure: Folder
}

const INDENT_SIZE = 16

const formatUnitCount = (count: number) =>
    `${count} ${count === 1 ? 'unit' : 'units'}`

export const AdvancedLoriTree: React.FC<AdvancedLoriTreeProps> = ({
    infrastructure,
}: AdvancedLoriTreeProps) => {
    const classes = useStepLoriStyles()

    const filteredInfrastructure = useMemo(
        () =>
            deepCopyFolder(infrastructure, (unit) =>
                isScheduleVisible(unit.visibility),
            ),
        [infrastructure],
    )

    const renderFolder = (folder: Folder, depth: number): JSX.Element => {
        const unitCount = folder.units?.length ?? 0

        return (
            <div
                key={folder.id}
                className={classes.treeNode}
                style={{ paddingLeft: depth * INDENT_SIZE }}
            >
                <div className={classes.treeNodeRow}>
                    <Typography
                        component="div"
                        className={classes.treeNodeName}
                    >
                        {folder.name}
                    </Typography>
                    {unitCount > 0 && (
                        <Typography
                            component="div"
                            className={classes.treeNodeUnits}
                        >
                            {formatUnitCount(unitCount)}
                        </Typography>
                    )}
                </div>
                {folder.children?.length > 0 && (
                    <div className={classes.treeNodeChildren}>
                        {folder.children.map((child) =>
                            renderFolder(child, depth + 1),
                        )}
                    </div>
                )}
            </div>
        )
    }

    if (filteredInfrastructure === null) {
        return (
            <>
                <Typography component="div" className={classes.treeHeader}>
                    Building layout
                </Typography>
                <div className={classes.treeBody} />
            </>
        )
    }

    return (
        <>
            <Typography component="div" className={classes.treeHeader}>
                Building layout
            </Typography>
            <div className={classes.treeBody}>
                {renderFolder(filteredInfrastructure, 0)}
            </div>
        </>
    )
}
